import { Injectable, signal } from "@angular/core";
import { ComponentPortal } from "@angular/cdk/portal";
import { select } from "@ngxs/store";
import { LayoutState } from "@dis/shared";

@Injectable({
  providedIn: "root",
})
export class ActionSidenavService {
  private readonly _portal = signal<ComponentPortal<any> | null>(null);
  readonly portal = this._portal.asReadonly();

  private readonly _actionData = signal<any>(null);
  readonly actionData = this._actionData.asReadonly();

  private readonly _isOpen = signal(false);
  readonly isOpen = this._isOpen.asReadonly();

  private readonly isHandset = select(LayoutState.isHandset);

  // False while the panel opens or closes because a page set or cleared its actions, so the content width changes
  // in one step instead of animating under a page that's still laying out (AG Grid redraws on every frame).
  private readonly _animate = signal(true);
  readonly animate = this._animate.asReadonly();

  private pendingClose: ReturnType<typeof setTimeout> | null = null;

  // Set Actions into action-sidenav
  setPortal(portal: ComponentPortal<any> | null, data?: any) {
    this.cancelPendingClose();
    this._portal.set(portal);
    this._actionData.set(data || null);

    // Auto-open on desktop when routing to a page that uses it — but leave mobile alone,
    // where it should stay closed by default until the user opens it themselves.
    if (portal && !this.isHandset()) {
      this.setOpen(true, false);
    }
  }

  // Clear Actions from action-sidenav. The close waits a tick: during navigation the next page sets its own actions
  // right after the old page clears them, and the panel then stays open instead of closing and reopening.
  clearPortal() {
    this._portal.set(null);
    this._actionData.set(null);
    this.cancelPendingClose();
    this.pendingClose = setTimeout(() => {
      this.pendingClose = null;
      if (!this._portal()) this.setOpen(false, false);
    });
  }

  // Open action-sidenav
  open() {
    this.setOpen(true, true);
  }

  // Close action-sidenav
  close() {
    this.setOpen(false, true);
  }

  toggle() {
    this.setOpen(!this._isOpen(), true);
  }

  private setOpen(open: boolean, animate: boolean) {
    this._animate.set(animate);
    this._isOpen.set(open);
  }

  private cancelPendingClose() {
    if (this.pendingClose === null) return;
    clearTimeout(this.pendingClose);
    this.pendingClose = null;
  }
}
